import { Box, ButtonGroup, Drawer, Grid, IconButton, Tooltip, Zoom } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Create, Replay, ThumbDownAlt, ThumbUpAlt } from "@material-ui/icons";
import { useAccessControl, useAuth, useVerifyPermission } from "@airborne/react-admin";
import * as React from "react";
import { FC, Fragment } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  ListProps,
  SelectInput,
  TextField,
  TextInput,
  useNotify,
  useRefresh,
} from "react-admin";
import { RDDDatetimeField } from "../../../Components/RDDDatetimeField";
import { EnumField } from "../../../../src/Components/EnumField";
import { ExcelExport } from "../../../../src/Helper/ExportExcel";
import { CustomDateField } from "../../../Components/CustomDateField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { GetCarrierType } from "../../../Enums/CarrierType";
import { RequestStatus } from "../../../Enums/RequestStatus";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { InitialReceivingListTitle, SourceRequestOrder } from "./Constant/Source";
import RequestOrderNotes from "./RequestOrderNotes";
import RequestStatusField from "../../../../src/Components/ReferenceFields/RequestStatus/RequestStatusField";
import { ConfirmButton } from "../../../Components/ConfirmButton";
import { DetailButton } from "../../../../src/Components/DetailButton";
import { RemarkTypes } from "../../../../src/Enums/RemarkTypes";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: "20%",
  },
}));

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const { remarkAddClick, ...rest } = props;
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { user } = useAuth();
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceRequestOrder);

  const acceptButtonClick = record => e => {
    record.requestStatus = RequestStatus.InitialProcess;
    // record.isPool = true;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };
  const rejectButtonClick = record => e => {
    record.requestStatus = RequestStatus.NotInitialProcess;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };
  function triggerInitialReceivingButtonClick(record) {
    dataProvider.asyncTriggerInitialReceiving(record).then(() => refresh());
  }
  const remarkOnClick = record => e => {
    e.stopPropagation();
    remarkAddClick?.(record);
  };
  const AcceptReceivingButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#12824C",
        }}
        size="medium"
        click={acceptButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Accept">
          <ThumbUpAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={"requestorders"} />;
  };
  const RejectReceivingButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#primary",
        }}
        size="medium"
        click={rejectButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Reject">
          <ThumbDownAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const TriggerInitialReceivingButton = ({ record }) => {
    return (
      <IconButton
        onClick={e => {
          e.stopPropagation();
          triggerInitialReceivingButtonClick(record);
        }}
        style={{
          color: "#b2b300",
        }}
      >
        <Tooltip TransitionComponent={Zoom} title="Initial Receiving Trigger">
          <Replay />
        </Tooltip>
      </IconButton>
    );
  };
  const RemarkButton = ({ record }) => {
    return (
      <IconButton style={{ width: 40 }} onClick={remarkOnClick(record)}>
        <Tooltip TransitionComponent={Zoom} title="Core Last Update">
          <Create />
        </Tooltip>
      </IconButton>
    );
  };

  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          {user?.isCustomer === false && record?.requestStatus === RequestStatus.PoolUserAccept && canCreate && (
            <>
              <AcceptReceivingButton record={record} />
              <TriggerInitialReceivingButton record={record} />
              <RejectReceivingButton record={record} />
              <RemarkButton record={record} />
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid {...rest} optimized basePath={SourceRequestOrder}>
        {<OptButtonGroup record={props} />}
        <RequestStatusField label="Status" />
        <TextField source="refNo" label="Ref No" />
        <TextField source="customerPo" label="Customer Po" />
        <RDDDatetimeField label="Request Delivery Date" />
        <TextField source="customerRequestComponent" label="Pn" />
        <TextField source="unserviceable.sn" label="Core Unit SN" />
        <TextField source="unserviceable.awb" label="Core Unit AWB" />
        <EnumField
          label={"Core Unit Forwarding Agency"}
          enumName={"GetCarrierType"}
          source={"unserviceable.carrierEnum"}
        />
        <CustomDateField source="receivedDate" label="Received Date" showTime />
        <TextField source="currentPbhNote" label="Current PBH Note" />
      </Datagrid>
    </Fragment>
  );
};

const InitialReceivingFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <TextInput label="Ref No" source="refNo__contains" alwaysOn />
    <TextInput label="Customer Po" source="customerPo__contains" alwaysOn />
    <TextInput label="Pn" source="customerRequestComponent__contains" alwaysOn />
    <TextInput label="Sn" source="unserviceable.sn__contains" alwaysOn />
    <TextInput label="Awb" source="unserviceable.awb__contains" alwaysOn />
    <SelectInput
      source="unserviceable.carrierEnum__eq"
      choices={GetCarrierType}
      label="Core Unit Forwarding Agency"
      optionText="Description"
      optionValue="Value"
      style={{ width: "285px" }}
      alwaysOn
      fullWidth
    ></SelectInput>
  </Filter>
);

const InitialReceivingList: FC<ListProps> = props => {
  useVerifyPermission("customroutes", "initialreceiving.read");
  const classes = useStyles();
  const [isMatchCreate, setIsMatchCreate] = React.useState(false);
  const [requestRemark, setRequestRemark] = React.useState<any>({});

  const ExportToExcel = data => {
    ExcelExport(
      [
        {
          header: "Status",
          key: "Status",
        },
        {
          header: "Ref No",
          key: "Ref No",
        },
        {
          header: "Customer Po",
          key: "Customer Po",
        },
        {
          header: "Requested Delivery Date",
          key: "Requested Delivery Date",
        },
        {
          header: "Pn",
          key: "Pn",
        },
        {
          header: "Core Unit Sn",
          key: "Core Unit Sn",
        },
        {
          header: "Core Unit AWB",
          key: "Core Unit AWB",
        },
        {
          header: "Core Unit Forwarding Agency",
          key: "Core Unit Forwarding Agency",
        },
        {
          header: "Received Date",
          key: "Received Date",
        },
      ],
      data.map(v => [
        v.requestStatus,
        v.refNo,
        v.customerPo,
        v.requestedDeliveryDate,
        v.customerRequestComponent,
        v.unserviceable?.sn,
        v.unserviceable?.awb,
        GetCarrierType.find(x => x.Value == v.unserviceable?.carrierEnum)?.Description,
        v.receivedDate != null ? v.receivedDate : "",
      ]),
      "Initial Receiving List"
    );
  };
  const remarkAddClick = record => {
    setIsMatchCreate(true);
    setRequestRemark(record);
  };
  const handleClose = () => {
    setIsMatchCreate(false);
  };
  return (
    <Grid container>
      <Grid item xs={isMatchCreate ? 9 : 12}>
        <PoolList
          {...props}
          hasCreate={false}
          bulkActionButtons={false}
          resource={SourceRequestOrder}
          basePath={SourceRequestOrder}
          title={InitialReceivingListTitle}
          filterDefaultValues={{
            isUnsvcInfo__eq: true,
            [`requestStatus =  ${RequestStatus.PoolUserAccept} || requestStatus = ${RequestStatus.NotInitialProcess}`]:
              "test",
          }}
          exporter={ExportToExcel}
          sort={DefaultCreatedDateSort}
          perPage={DefaultPerPageSize}
          filters={<InitialReceivingFilter />}
          // filters={
          //   <FilterToolbar
          //     dataSource={[
          //       {
          //         source: 'requestStatus',
          //         inputType: FilterInputType.Select,
          //         label: 'Status',
          //         optionText: 'label',
          //         optionValue: 'id',
          //         style: { width: '250px' },
          //         choices: [
          //           {
          //             label: 'Pool User Accept',
          //             id: RequestStatus.PoolUserAccept
          //           },
          //           {
          //             label: 'Not Initial Process',
          //             id: RequestStatus.NotInitialProcess
          //           }
          //         ]
          //       },
          //       {
          //         source: 'refNo',
          //         inputType: FilterInputType.Text,
          //         label: 'Ref No'
          //       },
          //       {
          //         source: 'customerPo',
          //         inputType: FilterInputType.Text,
          //         label: 'Customer Po'
          //       },
          //       {
          //         source: 'requestedDeliveryDate',
          //         inputType: FilterInputType.Date,
          //         label: 'Requested Delivery Date'
          //       },
          //       {
          //         source: 'customerRequestComponent',
          //         inputType: FilterInputType.Text,
          //         label: 'Pn'
          //       },
          //       {
          //         source: 'unserviceable.sn',
          //         inputType: FilterInputType.Text,
          //         label: 'Sn'
          //       },
          //       {
          //         source: 'unserviceable.awb',
          //         inputType: FilterInputType.Text,
          //         label: 'Awb'
          //       },
          //       {
          //         source: 'unserviceable.carrierEnum',
          //         inputType: FilterInputType.Select,
          //         label: 'Unserviceable Forwarding Agency',
          //         optionText: 'Description',
          //         optionValue: 'Value',
          //         style: { width: '250px' },
          //         choices: GetCarrierType
          //       },
          //       {
          //         source: 'receivedDate',
          //         inputType: FilterInputType.Date,
          //         label: 'Received Date'
          //       }
          //     ]}
          //   />
          // }
        >
          <TabbedDatagrid remarkAddClick={remarkAddClick} />
        </PoolList>
      </Grid>
      {isMatchCreate && (
        <Grid item xs={3}>
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="persistent"
            open={isMatchCreate}
            anchor="right"
            onClose={handleClose}
          >
            {isMatchCreate ? (
              <RequestOrderNotes
                onCancel={handleClose}
                request={requestRemark}
                resource="requestremarks"
                basePath="requestremarks"
                remarkType={RemarkTypes.PbhNote}
              />
            ) : null}
          </Drawer>
        </Grid>
      )}
    </Grid>
  );
};

export default InitialReceivingList;
